/**
 * Dataset Monitor Repository
 *
 * Manages the admin Dataset Monitor's three read-only endpoints: the
 * dataset list (index), a single dataset's drilldown (show), and a
 * dataset's paginated email sends.
 */

import { createRepository } from 'data/utils/createRepository';
import { getApiUrl } from 'data/services/apiConstants';
import { get } from 'data/services/apiService';

// ============================================
// Failure envelope (was: a silent deploy-skew guard)
// ============================================
// These endpoints may legitimately be absent on an older backend: per CLAUDE.md
// the frontend auto-deploys to production on push to master while the backend
// ships separately, so this page can go live before its own API does. Each
// failing call therefore RESOLVES rather than rejecting, so a refreshing page
// never raises per-cycle unhandled rejections.
//
// What it must NOT do is discard the failure. The original guard resolved with
// { data: null }, which rendered as "No datasets match the current filters" for
// every cause alike - a 404 from an older backend, a 504 when a query timed out,
// a 500 from an unmigrated database, a 401 from an expired session. That empty
// table sent three separate investigations chasing missing DATA when the request
// itself had failed. The resolved payload now carries an `error` the page can
// show, alongside the empty `datasets` the reducers expect.
const skewWarnings = {};

/**
 * Best-effort HTTP status. apiService.get() throws the parsed response BODY
 * rather than an Error carrying result.status, so the status is only available
 * when the body itself reports one (Cloudflare's error JSON does; Laravel's
 * does not). Absent that, the message still names the failure.
 */
const errorStatusOf = (error) => error?.status || error?.error_code || null;

const errorMessageOf = (error) => {
	if (typeof error === 'string') return error;
	return (
		error?.message ||
		error?.title ||
		error?.error ||
		'The request failed before any data was returned.'
	);
};

const catchDeploySkew = (key, message) => (error) => {
	if (!skewWarnings[key]) {
		skewWarnings[key] = true;
		// eslint-disable-next-line no-console
		console.warn(message, error);
	}

	return {
		data: {
			datasets: [],
			coverage: null,
			error: {
				status: errorStatusOf(error),
				message: errorMessageOf(error),
				apiUrl: getApiUrl(),
			},
		},
	};
};

// ============================================
// Create Repository
// ============================================
const datasetMonitorRepository = createRepository({
	endpoint: 'dataset-monitor/datasets',
	name: 'datasetMonitor',
	statePath: 'datasetMonitor',

	customMethods: {
		/**
		 * Get the dataset list for the monitor's main table.
		 * Usage: actions.getDatasets({ hours: 24 })
		 */
		getDatasets: ({ hours = 24, authToken, dispatch }) => {
			const params = { hours };
			return get({
				url: `${getApiUrl()}/api/dataset-monitor/datasets?${new URLSearchParams(params)}`,
				authToken,
				dispatch,
			}).catch(
				catchDeploySkew(
					'getDatasets',
					'DatasetMonitor: /dataset-monitor/datasets unavailable (old backend?) - table hidden',
				),
			);
		},

		/**
		 * Get a single dataset's drilldown: stat cards, the four email types
		 * in full, watched/failed counts, and the stale-properties list.
		 * Honours the same adjustable reporting window as getDatasets (backend
		 * validates it against [24, 72, 168, 720]) so the drawer's stat cards
		 * can never disagree with the window the table row was opened from.
		 * Usage: actions.getDataset({ key: 'bis_dob_violations', hours: 168 })
		 */
		getDataset: ({ key, hours = 24, authToken, dispatch }) => {
			const params = { hours };
			return get({
				url: `${getApiUrl()}/api/dataset-monitor/datasets/${key}?${new URLSearchParams(params)}`,
				authToken,
				dispatch,
			}).catch(
				catchDeploySkew(
					'getDataset',
					'DatasetMonitor: /dataset-monitor/datasets/{key} unavailable (old backend?) - drawer hidden',
				),
			);
		},

		/**
		 * Get a dataset's paginated email sends (Sent / Type / Recipient /
		 * Property / Records / Result).
		 * Usage: actions.getSends({ key: 'bis_dob_violations', page: 1, per_page: 25, type: 'alert', status: 'failed' })
		 */
		getSends: ({
			key,
			page = 1,
			per_page = 25,
			type = null,
			status = null,
			authToken,
			dispatch,
		}) => {
			const params = { page, per_page };
			if (type) params.type = type;
			if (status) params.status = status;
			return get({
				url: `${getApiUrl()}/api/dataset-monitor/datasets/${key}/sends?${new URLSearchParams(params)}`,
				authToken,
				dispatch,
			}).catch(
				catchDeploySkew(
					'getSends',
					'DatasetMonitor: /dataset-monitor/datasets/{key}/sends unavailable (old backend?) - sends list hidden',
				),
			);
		},
	},
});

// ============================================
// Exports
// ============================================
export const {
	name,
	statePath,
	thunks: datasetMonitorThunks,
	reducer: datasetMonitorReducer,
	actions: datasetMonitorActions,
	types: datasetMonitorTypes,
	selectors: datasetMonitorSelectors,
	useHook: useDatasetMonitor,
} = datasetMonitorRepository;

export default datasetMonitorRepository;
